<!--
@component

A component that renders SVG icons from the @mdi/js package
```svelte
  <Icon
    name='camera-outline'
    size='xl'
  />
```
-->
<script lang="ts">
import type { Component } from 'svelte';
import type { SVGAttributes } from 'svelte/elements';

import {
  type CustomIcon,
  IconPathsByName,
  type IconProps,
  type Size,
} from './icons';

const sizes: Record<Size, string> = {
  xs: 'w-3 h-3',
  sm: 'w-3.5 h-3.5',
  base: 'w-4 h-4',
  lg: 'w-5 h-5',
  xl: 'w-6 h-6',
  '2xl': 'w-7 h-7',
  '3xl': 'w-8 h-8',
  '4xl': 'w-9 h-9',
};

const {
  name,
  size = 'base',
  cx: extraClasses = '',
  ...restProps
}: IconProps = $props();

let allPaths = $state<CustomIcon[]>([]);
let IconComponent = $state<Component<SVGAttributes<SVGElement>>>();

$effect.pre(() => {
  const pathValue = IconPathsByName[name];
  if (typeof pathValue === 'string') {
    allPaths = [{ path: pathValue }];
    IconComponent = undefined;
  } else if (Array.isArray(pathValue)) {
    allPaths = pathValue.map((icon) => ({
      path: icon.path,
      opacity: 'opacity' in icon ? icon.opacity : undefined,
    }));
    IconComponent = undefined;
  } else if (typeof pathValue === 'function') {
    IconComponent = pathValue;
    allPaths = [];
  }
});
</script>

<!--
  Accessibility approach for icon svgs taken from:
  https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label
-->
{#if IconComponent}
  <IconComponent
    class={[sizes[size], extraClasses]}
    data-test-id={`icon-${name}`}
  />
{:else}
  <svg
    class={[sizes[size], extraClasses]}
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    data-testid={`icon-${name}`}
    {...restProps}
  >
    {#each allPaths as { path: dAttribute, opacity }, path (path)}
      <path
        d={dAttribute}
        {opacity}
        fill-rule="evenodd"
        fill="currentColor"
      />
    {/each}
  </svg>
{/if}
